

























import Vue from 'vue';
import ApplicationFormTemplate from '@/components/templates/applicationFormTemplate/ApplicationFormTemplate.vue';
import { modelService } from '@/services/form/modelService';
import { rules } from '@/services/form/rules';
import { ApplicationFormContent } from '@/api/interfaces/content/form/applicationFormContent';
import {
    createFormValidations,
    ValidationsCommonType,
    ValidationsValueType,
} from '@/services/form/validations';
import { contentServices } from '@/services/contentService';
import { configurationServices } from '@/services/configurationService';
import SpinnerArea from '@/components/molecules/spinnerArea/SpinnerArea.vue';
import { SpinnerState } from '@/enums/spinnerState';
import { Forms } from '@/types/forms/forms';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import { mergeWith as _mergeWith, mapValues as _mapValues } from 'lodash';
import { formsConfig } from '@/services/form/config';
import { applicationRepository } from '@/api';
import { Validation } from 'vuelidate';
import { ValidationProperties } from 'vue/types/vue';
import { validate as uuidValidate, v4 as uuidv4 } from 'uuid';
import { ApplicationFormConfiguration } from '@/api/interfaces/configuration/form/applicationFormConfiguration';
import { FormStepConfiguration } from '@/api/interfaces/configuration/form/base/formStepConfiguration';
import { FormFieldConfiguration } from '@/api/interfaces/configuration/form/base/formFieldConfiguration';
import { FileUploadConfiguration } from '@/api/interfaces/configuration/form/base/fileUploadConfiguration';
import { appConfiguration } from '@/services/form/appConfiguration';
import { debugDto } from '@/debug/form/application';
import { convertFormConfigToFormDto, FormDto } from '@/services/form/dto';
import { authService } from '@/services/authService';
import { ServiceSuccess } from '@/api/interfaces/serviceSuccess';
import { ServiceError } from '@/api/interfaces/serviceError';
import { dialogueMessageService } from '@/services/dialogueMessageService';
import router from '@/router';
import { MessageType } from '@/enums/messageType';
import { MessageDialogueActions } from '@/enums/messageDialogueActions';
import { applyApplicationEditActions } from '@/services/form/editActions/application';
import { alertService } from '@/services/alertService';
import { FormContent } from '@/api/interfaces/content/form/formContent';
import { FormOption } from '@/types/forms/formOption';
import {
    ApplicationDebugPresetId,
    getApplicationDebugPreset,
} from '@/services/form/applicationPresets/applicationDebugPresets';
import { fillDebugUploads } from '@/services/form/applicationPresets/applicationDebugUploads';

export default Vue.extend({
    name: 'ApplicationForm',
    components: {
        ApplicationFormTemplate,
        SpinnerArea,
    },
    provide(): {
        editStep: (identifier: string) => void;
        scrollToStep: (identifier: string) => void;
    } {
        return {
            editStep: this.editStep,
            scrollToStep: this.scrollToStep,
        };
    },
    data() {
        return {
            spinner: SpinnerState.opaque,
            applicationFormContent: null as ApplicationFormContent | null,
            applicationFormConfiguration: null as ApplicationFormConfiguration | null,
            rawEditModel: {} as Forms,
            rawEditValidations: {} as ValidationsCommonType,
            contentLoaded: false,
        };
    },
    computed: {
        model(): ApplicationForm {
            return modelService.model.application;
        },
        debugDto(): FormDto {
            return this.debugEnabled ? debugDto : {};
        },
        debugEnabled(): boolean {
            return (
                authService.isAuthenticated && authService.hasRoles('DEA Debug')
            );
        },
        dto(): FormDto {
            return convertFormConfigToFormDto(this.model);
        },
        formsConfig(): ApplicationForm {
            return formsConfig.application;
        },
        editModel(): ApplicationForm {
            return this.rawEditModel.application;
        },
        rules() {
            return rules.application;
        },
        validations(): Validation & ValidationProperties<unknown> {
            return modelService.validations.application;
        },
        editValidations(): ValidationsValueType {
            return this.rawEditValidations.application;
        },
        currentStepIndex(): number {
            return this.model.enabledSteps.indexOf(this.model.currentStep);
        },
        maxReachedStepIndex(): number {
            return this.model.enabledSteps.indexOf(this.model.maxReachedStep);
        },
    },
    created(): void {
        modelService.resetModel(this);
        this.resetEditModels();
    },
    async mounted(): Promise<void> {
        alertService.clear();

        try {
            await this.initializeContent();
        } catch (error) {
            router.push('/fehler');
        }

        if (
            !this.$route.params.applicationId &&
            this.model.currentStep === '_init_'
        ) {
            this.model.applicationId = uuidv4();
            this.$router.replace(
                `${this.$route.path}/${this.model.applicationId}`,
            );
            this.model.lastModified = await applicationRepository.saveApplication(
                this.model,
            );
        }

        if (this.$route.params.applicationId) {
            if (uuidValidate(this.$route.params.applicationId)) {
                await applicationRepository
                    .getApplicationData(this.$route.params.applicationId)
                    .then(async (response) => {
                        response = modelService.migrateApplicationFormData(
                            response,
                        );
                        modelService.loadFormIntoModel(response, 'application');

                        // wait for rules to possibly disable steps
                        await this.rules.general(this.model);

                        this.advanceStep();
                    })
                    .catch((error: ServiceError) => {
                        this.showErrorMessageInDialogue(error.messageCode);
                    });
            } else {
                this.showErrorMessageInDialogue('applications.notFound');
            }
        }

        this.spinner = SpinnerState.off;
        this.scrollToCurrentStep();
    },
    methods: {
        async initializeContent(): Promise<void> {
            // Global
            const content = await contentServices.form.requestContentOnce();
            this.applicationFormContent = content.applicationFormContent;

            const configuration = await configurationServices.form.requestConfigurationOnce();
            this.applicationFormConfiguration =
                configuration.applicationFormConfiguration;
            this.prepareUploadFieldValidations();

            // Form steps
            this.initializeMeasuringConceptsContent(content);

            this.contentLoaded =
                !!this.applicationFormContent &&
                !!this.applicationFormConfiguration;
        },
        initializeMeasuringConceptsContent(content: FormContent): void {
            if (!this.applicationFormContent) {
                return;
            }

            this.model.measuringConcepts.measuringPointOperator.options = [];
            this.applicationFormContent.measuringConceptsContent.measuringPointOperator.options = {};

            for (const value of content.serviceDrivenContent
                .measuringPointOperatorsContent) {
                this.model.measuringConcepts.measuringPointOperator.options.push(
                    new FormOption({ key: value }),
                );
                this.applicationFormContent.measuringConceptsContent.measuringPointOperator.options[
                    value
                ] = {
                    label: value,
                };
            }
        },
        prepareUploadFieldValidations(): void {
            _mapValues(
                this.applicationFormConfiguration,
                (formStep: FormStepConfiguration) => {
                    _mapValues(
                        formStep.fields,
                        (field: FormFieldConfiguration) => {
                            const fileUploadConfiguration =
                                ((field.configurations.find(
                                    (c) => c.type === 'fileUploadConfiguration',
                                ) as unknown) as FileUploadConfiguration) ??
                                null;
                            if (fileUploadConfiguration) {
                                appConfiguration.fileUploadConfigurations[
                                    fileUploadConfiguration.scId
                                ] = fileUploadConfiguration.validations;
                            }
                        },
                    );
                },
            );
        },
        selectStep(stepId: string): void {
            this.model.currentStep = stepId;
            if (this.currentStepPassesMaxReachedStep()) {
                this.model.maxReachedStep = this.model.currentStep;
            }
        },
        currentStepPassesMaxReachedStep(): boolean {
            return this.currentStepIndex > this.maxReachedStepIndex;
        },
        async debugResetStep(stepIdentifier: string): Promise<void> {
            const enabledSteps = this.model.enabledSteps;
            const currentStepIndex = enabledSteps.indexOf(stepIdentifier);

            if (currentStepIndex <= 0) {
                return;
            }

            const previousStepIdentifier = enabledSteps[currentStepIndex - 1];
            modelService.removeDataAfterCurrentStep(
                previousStepIdentifier,
                'application',
            );
            modelService.resetStateAfterCurrentStep(
                previousStepIdentifier,
                'application',
                this,
            );

            this.model.currentStep = previousStepIdentifier;
            this.model.maxReachedStep = previousStepIdentifier;

            await this.rules.general(this.model);
            await this.scrollToCurrentStep();
        },
        async debugGoToStep(stepIdentifier: string): Promise<void> {
            if (!this.model.enabledSteps.includes(stepIdentifier)) {
                return;
            }

            this.model.currentStep = stepIdentifier;
            this.model.maxReachedStep = stepIdentifier;

            await this.scrollToCurrentStep();
        },
        async applyDebugPreset(id: ApplicationDebugPresetId): Promise<void> {
            if (!this.debugEnabled) {
                return;
            }

            const preset = getApplicationDebugPreset(id);
            if (
                !preset ||
                !window.confirm(
                    `Alle bisherigen Eingaben werden durch "${preset.label}" ersetzt. Fortfahren?`,
                )
            ) {
                return;
            }

            const applicationId = this.model.applicationId;
            const lastModified = this.model.lastModified;
            if (!applicationId || !this.applicationFormConfiguration) {
                return;
            }

            this.spinner = SpinnerState.opaque;
            try {
                modelService.resetFormIntoModel(
                    {
                        ...preset.dto,
                        applicationId,
                        lastModified,
                    },
                    'application',
                    this,
                );

                await this.$nextTick();
                await this.$nextTick();
                await this.rules.general(this.model);
                await this.$nextTick();

                await this.rules.documents(({
                    model: this.model.documents,
                    formModel: this.model,
                } as unknown) as Parameters<typeof rules.application.documents>[0]);
                await this.$nextTick();

                await fillDebugUploads(
                    this.model.documents,
                    this.applicationFormConfiguration.documentsConfiguration,
                    applicationId,
                    this.model,
                );

                this.model.currentStep = 'checkAndSend';
                this.model.maxReachedStep = 'checkAndSend';
                this.model.lastModified = await applicationRepository.saveApplication(
                    this.model,
                );
                this.resetEditModels();
                await this.scrollToCurrentStep();
            } finally {
                this.spinner = SpinnerState.off;
            }
        },
        resetEditModels(): void {
            this.rawEditModel = modelService.getModelClone();
            this.rawEditValidations = createFormValidations(
                this.rawEditModel,
                this,
            );
        },
        async submitForm(
            cb: (promise: Promise<ServiceSuccess | string>) => void,
        ): Promise<void> {
            if (this.model.editStep) {
                // apply edit actions, like calculating values after editing etc.
                applyApplicationEditActions({
                    formModel: this.model,
                    editModel: this.editModel,
                });

                // load data for edited step into real model
                _mergeWith(
                    this.model[this.model.editStep as keyof ApplicationForm],
                    this.editModel[
                        this.model.editStep as keyof ApplicationForm
                    ],
                );

                // // remove all data after current step
                // modelService.removeDataAfterCurrentStep(
                //     this.model.editStep,
                //     'application',
                // );

                // reset disabled state after current step
                modelService.resetStateAfterCurrentStep(
                    this.model.editStep,
                    'application',
                    this,
                );

                // reset current step
                this.model.currentStep = this.model.editStep;
                this.model.editStep = null;

                // reset edit model and validations, so we could edit again
                this.resetEditModels();
            }

            await this.rules.general(this.model);

            const saveServiceResponse = applicationRepository.saveApplication(
                this.model,
            );
            saveServiceResponse
                .then(async (response: string) => {
                    this.model.lastModified = response;

                    if (!this.advanceStep()) {
                        const sendServiceResponse = applicationRepository.sendApplication(
                            this.model,
                        );
                        sendServiceResponse
                            .then(() => {
                                // if there is no next step, call cb here to handle sendService success
                                cb(sendServiceResponse);

                                router.push('/danke');
                            })
                            .catch(() => {
                                // if there is no next step, call cb here to handle sendService error
                                cb(sendServiceResponse);
                            });
                    } else {
                        // if there is a next step, call cb here to handle saveService success
                        cb(saveServiceResponse);
                    }
                })
                .catch(() => {
                    // if saveService fails, we call cb here, to handle saveService error.
                    cb(saveServiceResponse);
                })
                .finally(this.scrollToCurrentStep);
        },
        advanceStep(): boolean {
            if (!this.hasNextStep()) {
                return false;
            }

            const nextStepId = this.model.enabledSteps[
                this.currentStepIndex + 1
            ];

            this.selectStep(nextStepId);
            return true;
        },
        hasNextStep(): boolean {
            return this.model.enabledSteps.length > this.currentStepIndex + 1;
        },
        editStep(identifier: string): void {
            this.model.editStep = identifier;
            this.resetEditModels();
        },
        cancelEdit(): void {
            this.model.editStep = null;
            this.resetEditModels();
        },
        showErrorMessageInDialogue(identifier: string) {
            dialogueMessageService.show(
                identifier,
                MessageType.error,
                MessageDialogueActions.toSafety |
                    MessageDialogueActions.newApplication,
            );
        },
        async scrollToStep(identifier: string) {
            await this.$nextTick();

            this.scrollToClass(`scroll-to-${identifier}-step`);
        },
        async scrollToCurrentStep() {
            await this.$nextTick();

            this.scrollToClass('scroll-to-current-step');
        },
        scrollToClass(className: string) {
            const step = document.querySelector(`.${className}`) as HTMLElement;

            if (step) {
                step.scrollIntoView();
            }
        },
    },
});
